/**
 * Shared changelog schema, types, and presentation metadata.
 *
 * This module is the single source of truth for the *shape* of the curated,
 * end-user-facing changelog. It is imported by:
 *   - the API routes (`/api/changelog`) to validate reads/writes,
 *   - the in-app viewer/editor to render and edit entries,
 *   - the project skill that converts `CHANGELOG.md` into `changelog.json`.
 *
 * It is framework-agnostic (no React) so it can run on both server and client.
 */

import { z } from "zod";

/** The kind of change an entry represents. Drives the timeline accent colour. */
export const ENTRY_TYPES = [
	"feature",
	"improvement",
	"fix",
	"performance",
	"security",
	"change",
	"deprecated",
	"docs",
] as const;

export type EntryType = (typeof ENTRY_TYPES)[number];

/**
 * Whether an entry is shown to end users (`public`) or kept for the team only
 * (`internal`). Internal entries are the "secret pushes" — they are stripped
 * from the response served to end users and only visible inside the editor.
 */
export const VISIBILITIES = ["public", "internal"] as const;
export type Visibility = (typeof VISIBILITIES)[number];

/** Maturity of a version, shown as a status pill in the viewer. */
export const RELEASE_STATUSES = ["stable", "rc", "beta", "alpha"] as const;
export type ReleaseStatus = (typeof RELEASE_STATUSES)[number];

export const changelogEntrySchema = z.object({
	/** Stable slug, e.g. `1.6.0-rc.1-retry-transcription`. Used as React key. */
	id: z.string().min(1),
	type: z.enum(ENTRY_TYPES),
	/** Short, user-facing headline. */
	title: z.string().min(1),
	/** Optional longer description rendered under the title. */
	body: z.string().default(""),
	visibility: z.enum(VISIBILITIES).default("public"),
});
export type ChangelogEntry = z.infer<typeof changelogEntrySchema>;

export const changelogReleaseSchema = z.object({
	/** Full release tag, e.g. `1.6.0-rc.3` or `1.6.0`. */
	tag: z.string().min(1),
	/** ISO date (`YYYY-MM-DD`). */
	date: z.string(),
	entries: z.array(changelogEntrySchema).default([]),
});
export type ChangelogRelease = z.infer<typeof changelogReleaseSchema>;

export const changelogVersionSchema = z.object({
	/** Marketing/minor version shown in the left rail, e.g. `1.6.0`. */
	version: z.string().min(1),
	/** ISO date of the latest release in this version. */
	date: z.string(),
	status: z.enum(RELEASE_STATUSES).default("stable"),
	/** Optional headline for the whole version. */
	title: z.string().default(""),
	/** Optional intro paragraph shown above the timeline. */
	summary: z.string().default(""),
	/** Release candidates / builds that make up this version, newest first. */
	releases: z.array(changelogReleaseSchema).default([]),
});
export type ChangelogVersion = z.infer<typeof changelogVersionSchema>;

export const changelogSchema = z.object({
	/** The version considered "current" (highlighted on open). */
	currentVersion: z.string(),
	/** ISO timestamp of the last edit (maintained by the API on write). */
	updatedAt: z.string().default(""),
	/** Versions, newest first. */
	versions: z.array(changelogVersionSchema),
});
export type Changelog = z.infer<typeof changelogSchema>;

/** Presentation metadata for each entry type (label + accent colours). */
export interface EntryTypeMeta {
	label: string;
	/** Solid accent colour for the timeline dot (works in light + dark). */
	dot: string;
	/** Tailwind classes for the type badge. */
	badge: string;
}

export const ENTRY_TYPE_META: Record<EntryType, EntryTypeMeta> = {
	feature: {
		label: "New",
		dot: "#10b981",
		badge:
			"bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20 dark:text-emerald-400",
	},
	improvement: {
		label: "Improved",
		dot: "#0ea5e9",
		badge:
			"bg-sky-500/10 text-sky-600 ring-1 ring-sky-500/20 dark:text-sky-400",
	},
	fix: {
		label: "Fixed",
		dot: "#f43f5e",
		badge:
			"bg-rose-500/10 text-rose-600 ring-1 ring-rose-500/20 dark:text-rose-400",
	},
	performance: {
		label: "Performance",
		dot: "#f59e0b",
		badge:
			"bg-amber-500/10 text-amber-600 ring-1 ring-amber-500/20 dark:text-amber-400",
	},
	security: {
		label: "Security",
		dot: "#ef4444",
		badge:
			"bg-red-500/10 text-red-600 ring-1 ring-red-500/20 dark:text-red-400",
	},
	change: {
		label: "Changed",
		dot: "#8b5cf6",
		badge:
			"bg-violet-500/10 text-violet-600 ring-1 ring-violet-500/20 dark:text-violet-400",
	},
	deprecated: {
		label: "Deprecated",
		dot: "#71717a",
		badge:
			"bg-zinc-500/10 text-zinc-600 ring-1 ring-zinc-500/20 dark:text-zinc-400",
	},
	docs: {
		label: "Docs",
		dot: "#14b8a6",
		badge:
			"bg-teal-500/10 text-teal-600 ring-1 ring-teal-500/20 dark:text-teal-400",
	},
};

/** Human label for a release status pill. */
export const RELEASE_STATUS_META: Record<
	ReleaseStatus,
	{ label: string; className: string }
> = {
	stable: {
		label: "Stable",
		className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
	},
	rc: {
		label: "Release candidate",
		className: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
	},
	beta: {
		label: "Beta",
		className: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
	},
	alpha: {
		label: "Alpha",
		className: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
	},
};

/**
 * Strip internal entries (and any version/release left empty as a result) so
 * the result is safe to serve to end users. Returns a new object; the input is
 * not mutated.
 */
export function toPublicChangelog(changelog: Changelog): Changelog {
	const versions = changelog.versions
		.map((version) => {
			const releases = version.releases
				.map((release) => ({
					...release,
					entries: release.entries.filter(
						(entry) => entry.visibility === "public"
					),
				}))
				.filter((release) => release.entries.length > 0);
			return { ...version, releases };
		})
		.filter((version) => version.releases.length > 0);

	return { ...changelog, versions };
}

/** Parse + validate unknown data into a `Changelog`, throwing on failure. */
export function parseChangelog(data: unknown): Changelog {
	return changelogSchema.parse(data);
}

const LEADING_V_RE = /^v/;
const PRERELEASE_SUFFIX_RE = /-.*$/;

/** Marketing version — strip a leading `v` and any prerelease suffix. */
export function marketingVersion(version: string): string {
	return version.replace(LEADING_V_RE, "").replace(PRERELEASE_SUFFIX_RE, "");
}

/**
 * Compare two versions by their `MAJOR.MINOR.PATCH` parts. Negative when `a`
 * is older, positive when newer, 0 when equal. Used to keep versions sorted
 * newest-first when a new build version is auto-inserted.
 */
export function compareVersions(a: string, b: string): number {
	const left = marketingVersion(a).split(".");
	const right = marketingVersion(b).split(".");
	for (const index of [0, 1, 2]) {
		const da = Number.parseInt(left[index] ?? "0", 10) || 0;
		const db = Number.parseInt(right[index] ?? "0", 10) || 0;
		if (da !== db) {
			return da - db;
		}
	}
	return 0;
}
